<template>
  <!-- 
    TextInput Component - A reusable form input with validation
    
    Features:
    - Built-in vee-validate integration for form validation
    - Support for input masking (phone numbers, dates, etc.)
    - Accessibility features (ARIA labels, screen reader support)
    - Automatic error display and styling
    - Flexible validation rules (required, min/max length, pattern matching)
  -->

  <!-- Label with required/optional indicator -->
  <label
    v-if="props.label"
    class="u-block u-textBlackAlt u-spaceBottom04"
    :class="labelClass"
    :for="props.id"
  >
    {{ props.label }}
    <!-- Required field indicator with asterisk -->
    <span v-if="props.required"
      ><span class="u-hiddenVisually">(required)</span><sup class="u-textRed">*</sup></span
    >
    <!-- Optional field indicator for screen readers -->
    <span v-else class="u-hiddenVisually">(optional)</span>
  </label>
  <!-- Masked input field (for phone numbers, dates, etc.) -->
  <input
    v-if="props.mask"
    :class="`Input ${props.inputClass} ${props.required ? 'is-required' : ''} ${errorMessage ? 'u-borderColorRed' : ''}`"
    :id="props.id"
    :name="props.name || props.id"
    :required="props.required"
    :rules="props.rules"
    :value="props.modelValue"
    :type="props.type || 'text'"
    :placeholder="props.placeholder"
    :minlength="props.minlength"
    :maxlength="props.maxlength"
    :pattern="props.pattern"
    v-the-mask="props.mask"
    :aria-describedby="props.id + 'Help'"
    :disabled="props.disabled || false"
    v-bind="$attrs"
    :hideErrorMessage="props.hideErrorMessage"
    @input="handleInput"
    @blur="handleBlur"
  />
  <!-- Standard text input field -->
  <input
    v-else
    :class="`Input ${props.inputClass} ${props.required ? 'is-required' : ''} ${errorMessage ? 'u-borderColorRed' : ''}`"
    :id="props.id"
    :name="props.name || props.id"
    :required="props.required"
    :value="props.modelValue"
    :type="props.type || 'text'"
    :min="props.min"
    :max="props.max"
    :placeholder="props.placeholder"
    :minlength="props.minlength"
    :maxlength="props.maxlength"
    :pattern="props.pattern"
    :aria-describedby="props.id + 'Help'"
    :disabled="props.disabled || false"
    v-bind="$attrs"
    :hideErrorMessage="props.hideErrorMessage"
    @input="handleInput"
    @blur="handleBlur"
  />
  <!-- Help text for additional context -->
  <small
    class="u-block u-textShrink2 u-textGrey u-spaceTop06"
    v-if="props.helpText"
    :id="props.id + 'Help'"
    >{{ props.helpText }}</small
  >
  <!-- Validation error message (show[n when field is invalid) -->
  <p
    class="input-error u-textShrink1 u-textRed u-spaceTop02"
    v-if="errorMessage || hideErrorMessage"
    role="alert"
  >
    {{ errorMessage }}
  </p>
</template>

<script setup>
/**
 * TextInput Component
 *
 * A reusable form input component with built-in validation using vee-validate.
 * Supports input masking, accessibility features, and flexible validation rules.
 *
 * Usage:
 * <TextInput
 *   id="email"
 *   name="email"
 *   label="Email Address"
 *   v-model="formData.email"
 *   required
 *   :minlength="5"
 *   pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
 * />
 */

// Prevent attribute inheritance to manually control where they're applied
// Import dependencies
import { mask as vTheMask } from 'vue-the-mask' // For input masking functionality
import { useAttrs, computed, watch } from 'vue' // Vue composition API
import { useField } from 'vee-validate'

defineOptions({ inheritAttrs: false }) // Form validation

// Get any additional attributes passed to the component
const attrs = useAttrs()

/**
 * Component Props
 * Defines all the configurable options for the TextInput component
 */
const props = defineProps({
  // The current value of the input (used with v-model)
  modelValue: {
    type: String,
    default: '',
  },
  // Unique identifier for the input (required for accessibility)
  id: {
    type: String,
    required: true,
  },
  // Form field name (defaults to id if not provided)
  name: {
    type: String,
    default: '',
  },
  // Form field type (defaults to 'text' if not provided)
  type: {
    type: String,
    default: 'text',
  },
  // Visible label text above the input
  label: {
    type: String,
    default: '',
  },
  // Placeholder text shown inside the input
  placeholder: {
    type: String,
    default: '',
  },
  // Additional help text shown below the input
  helpText: {
    type: String,
    default: '',
  },
  // Whether the field is required for form submission
  required: {
    type: Boolean,
    default: false,
  },
  // Minimum number of characters required
  minlength: {
    type: [String, Number],
    default: null,
  },
  // Maximum number of characters allowed
  maxlength: {
    type: [String, Number],
    default: null,
  },
  // Regex pattern for validation
  pattern: {
    type: String,
    default: null,
  },
  // Minimum numeric value (for number inputs)
  min: {
    type: [String, Number],
    default: null,
  },
  // Maximum numeric value (for number inputs)
  max: {
    type: [String, Number],
    default: null,
  },
  // Input mask pattern (e.g., phone number: '(###) ###-####')
  mask: {
    type: [String, Array],
    default: null,
  },
  // Disabled attribute to disable the input
  disabled: {
    type: Boolean,
    default: false,
  },
  // Additional CSS classes for the label element
  labelClass: {
    type: String,
    default: '',
  },
  // Input Class
  inputClass: {
    type: String,
    default: '',
  },
  // Whether to always show the error message space (to prevent layout shift)
  hideErrorMessage: {
    type: Boolean,
    default: false,
  },
})

/**
 * Component Events
 * Events that this component can emit to parent components
 */
const emit = defineEmits(['update:modelValue', 'blur'])

/**
 * Validation Rules Generator
 * Creates vee-validate rules based on the component props
 * Returns an array of validation functions
 */
const validationRules = computed(() => {
  const rules = []

  // Required field validation
  if (props.required) {
    rules.push((value) => {
      if (
        value === undefined ||
        value === null ||
        value === '' ||
        (typeof value === 'string' && value.trim() === '')
      ) {
        return 'This field is required'
      }
      return true
    })
  }

  // Minimum length validation
  if (props.minlength) {
    rules.push((value) => {
      if (value && value.length < props.minlength) {
        return `Must be at least ${props.minlength} characters`
      }
      return true
    })
  }

  // Maximum length validation
  if (props.maxlength) {
    rules.push((value) => {
      if (value && value.length > props.maxlength) {
        return `Must be no more than ${props.maxlength} characters`
      }
      return true
    })
  }

  // Pattern matching validation (regex)
  if (props.pattern) {
    rules.push((value) => {
      if (value && !new RegExp(props.pattern).test(value)) {
        return 'Invalid format'
      }
      return true
    })
  }

  // Type-specific validation
  if (props.type === 'email') {
    rules.push((value) => {
      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        return 'Please enter a valid email address'
      }
      return true
    })
  }

  if (props.type === 'tel' || props.type === 'phone') {
    rules.push((value) => {
      // 1. Let the 'required' rule handle empty values.
      if (!value || !value.length) {
        return true
      }
      // 2. Sanitize the input to get only digits.
      const digits = String(value).replace(/\D/g, '')

      // 3. Explicitly check if the number of digits is 10 or 11. THIS IS THE KEY FIX.
      if (digits.length < 10 || digits.length > 11) {
        return 'The phone number must have 10 or 11 digits.'
      }

      // 4. Now, validate the pattern for North American numbers.
      const phoneRegex = /^(1)?([2-9]\d{2})([2-9]\d{2})(\d{4})$/
      if (!phoneRegex.test(digits)) {
        return 'Please enter a valid US phone number (e.g., area code cannot start with 0 or 1).'
      }

      // 5. If all checks pass, the number is valid.
      return true
    })
  }

  if (props.type === 'url') {
    rules.push((value) => {
      if (value && !/^https?:\/\/.+\..+/.test(value)) {
        return 'Please enter a valid URL'
      }
      return true
    })
  }

  if (props.type === 'number') {
    rules.push((value) => {
      if (value && isNaN(Number(value))) {
        return 'Please enter a valid number'
      }
      return true
    })

    // Min/Max validation for number inputs
    if (props.min !== null && props.min !== undefined) {
      rules.push((value) => {
        if (value && Number(value) < Number(props.min)) {
          return `Value must be at least ${props.min}`
        }
        return true
      })
    }

    if (props.max !== null && props.max !== undefined) {
      rules.push((value) => {
        if (value && Number(value) > Number(props.max)) {
          return `Value must be no more than ${props.max}`
        }
        return true
      })
    }
  }

  return rules
})

/**
 * Vee-Validate Field Setup
 * Integrates the input with vee-validate for form validation
 */
const {
  errorMessage, // Current validation error message (reactive)
  value: fieldValue, // Internal field value managed by vee-validate
  handleBlur: veeHandleBlur, // Vee-validate's blur handler
  handleChange, // Updates the internal field value
  validate, // Manually trigger validation
} = useField(props.name || props.id, validationRules, {
  initialValue: props.modelValue, // Set initial value from prop
  validateOnValueUpdate: false, // Don't validate on every keystroke
})

/**
 * Input Event Handler
 * Handles user input and updates both vee-validate and parent component
 */
const handleInput = (event) => {
  const newValue = event.target.value
  handleChange(newValue) // Update vee-validate's internal value
  emit('update:modelValue', newValue) // Emit to parent (for v-model)
}

/**
 * Blur Event Handler
 * Triggers validation when user leaves the field
 */
const handleBlur = async (event) => {
  await validate() // Trigger validation on blur
  veeHandleBlur(event) // Call vee-validate's blur handler
  emit('blur', event) // Emit blur event to parent
}

/**
 * Prop-to-Field Synchronization
 * Keeps vee-validate's internal value in sync with the modelValue prop
 * This is important when the parent component updates the value programmatically
 */
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue !== fieldValue.value) {
      handleChange(newValue)
    }
  },
)
</script>
